import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { BrandMark } from "../components/Logo";
import { WhatsAppSupport } from "../components/WhatsAppSupport";
import { Loader2, LogIn } from "lucide-react";

const DEFAULT_HERO = "/ubbs_hero_icemint.jpg";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [pageCfg, setPageCfg] = useState({ headline: "", subtext: "", has_image: false });

  useEffect(() => {
    fetch(`${API_BASE}/public/login-page`)
      .then((r) => r.json())
      .then((d) => setPageCfg(d))
      .catch(() => {});
  }, []);

  const heroUrl = pageCfg.has_image
    ? `${API_BASE}/public/login-image?v=${pageCfg.updated_at || ""}`
    : DEFAULT_HERO;
  const headline = (pageCfg.headline || "").trim();
  const subtext = (pageCfg.subtext || "").trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLongWait(false);
    // After 2s of loading, show a friendly "waking up server" hint
    const longWaitTimer = setTimeout(() => setLongWait(true), 2000);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome ${u.name || u.email}`);
      const dest = u.role === "admin" ? "/admin" : u.role === "client" ? "/client" : "/promoter";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      clearTimeout(longWaitTimer);
      setLoading(false);
      setLongWait(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative grid grid-cols-1 lg:grid-cols-2">
      {/* Mobile-only subtle glass background */}
      <div className="absolute inset-0 lg:hidden">
        <img src={heroUrl} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/35 backdrop-blur-[1px]" />
      </div>
      {/* Left – liquid glass hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img
          src={heroUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/20" />
        <div className="relative">
          <div className="inline-flex glass-panel px-4 py-3">
            <BrandMark />
          </div>
        </div>
        <div className="relative">
          <div
            className="glass-panel p-8 max-w-lg"
            style={{ background: "rgba(255,255,255,0.45)" }}
            data-testid="login-hero-text-panel"
          >
            {headline ? (
              <>
                <h1 className="font-display font-bold text-3xl xl:text-4xl leading-[1.1] tracking-tight text-slate-900">
                  {headline}
                </h1>
                {subtext && (
                  <p className="mt-4 text-slate-700 max-w-md text-balance whitespace-pre-line">
                    {subtext}
                  </p>
                )}
              </>
            ) : (
              <>
                <h1 className="font-display font-bold text-4xl xl:text-5xl leading-[1.05] tracking-tight text-slate-900">
                  Field force,
                  <br />
                  <span className="text-slate-500">crystal clear.</span>
                </h1>
                <p className="mt-4 text-slate-600 max-w-md text-balance">
                  Track promoters in real-time. Verify location at check-in.
                  Capture stock and customer insight at the shelf.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="relative text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">
          UBBS · Consumer Engagement Platform
        </div>
      </div>

      {/* Right – frosted form */}
      <div className="relative flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {headline && (
            <div
              className="lg:hidden glass-panel rounded-3xl p-5 mb-4"
              style={{ background: "rgba(255,255,255,0.5)" }}
              data-testid="login-mobile-text-panel"
            >
              <div className="font-display font-bold text-xl tracking-tight text-slate-900">
                {headline}
              </div>
              {subtext && (
                <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-line">{subtext}</p>
              )}
            </div>
          )}
          <div className="glass-panel rounded-3xl p-8 sm:p-10">
          <div className="lg:hidden mb-8">
            <BrandMark />
          </div>
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] font-medium text-slate-400">
              Step 01 / Authentication
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-2 tracking-tight">
              Sign in to continue
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Use credentials issued by your administrator.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form" autoComplete="on">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@promotify.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-2 h-12 rounded-2xl"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-2 h-12 rounded-2xl"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-700 text-white font-semibold tracking-wide"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin h-5 w-5" />
                  {longWait ? "Waking up server…" : "Signing in…"}
                </span>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" /> Sign in
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 border-t border-slate-200/70 pt-5 flex justify-center">
            <WhatsAppSupport />
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
